import React, { useEffect, useRef } from "react";

export default function VaultModalShell({
    children,
    onClose,
    wide = false,
    title = "Vault dialog",
    dismissOnBackdrop = true,
}: {
    children: React.ReactNode;
    onClose: () => void;
    wide?: boolean;
    title?: string;
    dismissOnBackdrop?: boolean;
}) {
    const panelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        const timer = window.setTimeout(() => panelRef.current?.focus(), 0);
        return () => {
            window.clearTimeout(timer);
            previous?.focus?.();
        };
    }, []);

    const trapFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Tab") return;
        const panel = panelRef.current;
        if (!panel) return;
        const focusable = Array.from(
            panel.querySelectorAll<HTMLElement>(
                "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
            )
        ).filter((node) => !node.hasAttribute("disabled") && node.getAttribute("aria-hidden") !== "true");
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm"
            data-testid="vault-modal-backdrop"
            onClick={dismissOnBackdrop ? onClose : undefined}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                onKeyDown={trapFocus}
                className={`bs-vault-modal-panel max-h-[92vh] w-full overflow-y-auto rounded-lg border border-cyan-900/50 bg-gradient-to-br from-neutral-950/72 via-black/68 to-cyan-950/20 shadow-[0_0_90px_rgba(8,145,178,0.20)] backdrop-blur-2xl ${
                    wide ? "max-w-3xl" : "max-w-md"
                }`}
                onClick={(event) => event.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}
